import MicIcon from '@/assets/icons/mic.svg?react'
import { FormSelect } from '@/features/missions/components/FormSelect'
import { Button, Spinner, Text } from '@/ui-kit'
import { type MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import clsx from 'clsx'
import { ChevronDown } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { NectarAudioPlayer } from '../shared/NectarAudioPlayer'
import { LiveWaveform } from './LiveWaveform'
import type { MicCheckResult, MicCheckThresholds } from './micCheckAnalysis'
import { DEFAULT_PROMPT, ISSUE_MESSAGES } from './micCheckMessages'
import { useMicCheck } from './useMicCheck'

export type MicCheckMetric = 'noise' | 'snr' | 'voice' | 'clip'

const ALL_METRICS: MicCheckMetric[] = ['noise', 'snr', 'voice', 'clip']

export interface MicCheckPanelProps {
  prompt?: string
  kind?: 'LANGUAGE' | 'CONSENT'
  /** Grade recording quality (noise/SNR/clip) and gate on it. Off for consent — capture only. */
  gradeQuality?: boolean
  durationSec?: number
  showDevicePicker?: boolean
  showDetails?: boolean
  visibleMetrics?: MicCheckMetric[]
  blockOnFail?: boolean
  thresholds?: Partial<MicCheckThresholds>
  onPass: (result: MicCheckResult, blob: Blob | null) => void
  onFail?: (result: MicCheckResult) => void
  onDismiss?: (result: MicCheckResult, blob: Blob | null) => void
}

const METRIC_LABELS: Record<MicCheckMetric, MessageDescriptor> = {
  noise: msg`Background noise`,
  snr: msg`Signal-to-noise`,
  voice: msg`Voice level`,
  clip: msg`Clipping`
}

function metricValue(metric: MicCheckMetric, r: MicCheckResult): { text: string; good: boolean } {
  switch (metric) {
    case 'noise':
      return { text: `${r.noiseFloorDb.toFixed(0)} dB`, good: r.noiseFloorDb < -45 }
    case 'snr':
      return { text: `${r.snr.toFixed(0)} dB`, good: r.snr >= 20 }
    case 'voice':
      return { text: `${r.voiceDb.toFixed(0)} dB`, good: r.voiceDb > -30 && r.voiceDb < -6 }
    case 'clip':
      return {
        text: r.clippedSamples === 0 ? 'none' : `${r.clippedSamples} samples`,
        good: r.clippedSamples === 0
      }
  }
}

export function MicCheckPanel({
  prompt,
  kind = 'LANGUAGE',
  gradeQuality = true,
  durationSec = 12,
  showDevicePicker = true,
  showDetails = false,
  visibleMetrics = ALL_METRICS,
  blockOnFail = false,
  thresholds,
  onPass,
  onFail,
  onDismiss
}: MicCheckPanelProps) {
  const { t } = useLingui()
  const promptText = prompt ?? t(DEFAULT_PROMPT)
  const mic = useMicCheck({ durationSec, thresholds })
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [playProgress, setPlayProgress] = useState(0)
  const [hasPlayed, setHasPlayed] = useState(false)
  const [playbackFailed, setPlaybackFailed] = useState(false)
  const firedResultRef = useRef<string | null>(null)

  useEffect(() => {
    if (mic.phase === 'recording' || mic.phase === 'idle') {
      setHasPlayed(false)
      setPlaybackFailed(false)
    }
  }, [mic.phase])

  useEffect(() => {
    setHasPlayed(false)
    setPlaybackFailed(false)
    setPlayProgress(0)
  }, [mic.recordedUrl])

  useEffect(() => {
    if (mic.phase !== 'result' || !mic.result) return
    const sig = `${mic.result.ok}:${mic.result.issue}`
    if (firedResultRef.current === sig) return
    firedResultRef.current = sig
    if (gradeQuality && !mic.result.ok) onFail?.(mic.result)
  }, [gradeQuality, mic.phase, mic.result, onFail])

  const handlePass = () => {
    if (mic.result) onPass(mic.result, mic.recordedBlob)
  }

  const handleDismiss = () => {
    if (mic.result) onDismiss?.(mic.result, mic.recordedBlob)
  }

  return (
    <div className="mx-auto w-full max-w-[600px]" style={{ animation: 'nectar-slide-up 0.4s ease-out' }}>
      <div className="max-mobile:px-4 max-mobile:py-6 mt-6 flex flex-col items-center gap-6 rounded-2xl border border-solid border-gray-100 px-8 py-10">
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="text-h5">
            {kind === 'CONSENT' ? <Trans>Confirm your consent</Trans> : <Trans>Check your mic</Trans>}
          </div>
          <div className="text-body-2 text-gray-80">
            {kind === 'CONSENT' ? (
              <Trans>Read the statement aloud to confirm you agree to the project rules.</Trans>
            ) : (
              <Trans>Read the phrase aloud — we&rsquo;ll play it back so you can hear yourself.</Trans>
            )}
          </div>
        </div>

        {(mic.phase === 'idle' || mic.phase === 'requesting' || mic.phase === 'error') && (
          <div className="flex w-full flex-col items-center gap-5">
            <div className="w-full rounded-xl border border-solid border-gray-100 p-4 text-center">
              <Text type="body3-regular" className="text-gray-90 mb-2 block tracking-wider uppercase">
                <Trans>Read this aloud</Trans>
              </Text>
              <Text type="h5" className="text-gray-70 block leading-snug">
                &ldquo;{promptText}&rdquo;
              </Text>
            </div>

            <div className="relative flex items-center justify-center" style={{ width: 96, height: 96 }}>
              <div
                className="bg-red absolute inset-0 rounded-full"
                style={{ animation: 'nectar-pulse-ring 2s ease-out infinite' }}
              />
              <div
                className="bg-red absolute inset-0 rounded-full opacity-0"
                style={{ animation: 'nectar-pulse-ring 2s ease-out infinite 0.8s' }}
              />
              <button
                type="button"
                onClick={() => void mic.start()}
                disabled={mic.phase === 'requesting'}
                className="bg-red relative z-1 flex size-20 items-center justify-center rounded-full text-white shadow-lg transition-all duration-200 hover:scale-105 disabled:opacity-70"
              >
                <MicIcon className="h-10" />
              </button>
            </div>

            <Text type="body3-regular" className="text-gray-90 tracking-wider uppercase">
              {mic.phase === 'requesting' ? t`Requesting microphone…` : t`Tap to record`}
            </Text>

            {mic.error && (
              <Text type="body2-regular" className="text-red text-center">
                {mic.error}
              </Text>
            )}
          </div>
        )}

        {mic.phase === 'recording' && (
          <div className="flex w-full flex-col items-center gap-5">
            <div className="w-full rounded-xl border border-solid border-gray-100 p-4 text-center">
              <Text type="body3-regular" className="text-gray-90 mb-2 block tracking-wider uppercase">
                <Trans>Read this aloud</Trans>
              </Text>
              <Text type="h5" className="text-gray-70 block leading-snug">
                &ldquo;{promptText}&rdquo;
              </Text>
            </div>
            <div className="w-full">
              <LiveWaveform analyser={mic.analyser} active className="block h-24 w-full" />
            </div>
            <div className="flex items-center gap-3">
              <span
                className="bg-red inline-block size-2 rounded-full"
                style={{ animation: 'nectar-pulse-ring 1s ease-in-out infinite' }}
              />
              <Text type="body3-regular" className="text-red tracking-wider uppercase">
                <Trans>Recording</Trans>
              </Text>
              <Text type="body3-regular" className="text-gray-80">
                {mic.elapsedSec.toFixed(1)}s
              </Text>
            </div>
          </div>
        )}

        {mic.phase === 'analyzing' && (
          <div className="flex flex-col items-center gap-4 py-6">
            <Spinner size="small" />
            <Text type="body1-regular" className="text-gray-80">
              <Trans>Checking your recording…</Trans>
            </Text>
          </div>
        )}

        {mic.phase === 'result' && mic.result && (
          <div className="flex w-full flex-col gap-4">
            {gradeQuality && (
              <div
                className={clsx(
                  'rounded-xl border border-solid p-4',
                  mic.result.ok ? 'border-green bg-green/5' : 'border-yellow bg-yellow/10'
                )}
              >
                <Text
                  type="body3-regular"
                  className={clsx('mb-2 block tracking-wider uppercase', mic.result.ok ? 'text-green' : 'text-gray-70')}
                >
                  {mic.result.ok ? <Trans>You sound great</Trans> : <Trans>One quick fix</Trans>}
                </Text>
                <div className="text-subtitle text-gray-70 mb-1 block font-semibold">
                  {mic.result.ok ? <Trans>Clear and clean.</Trans> : t(ISSUE_MESSAGES[mic.result.issue!].title)}
                </div>
                {!mic.result.ok && (
                  <Text type="body2-regular" className="text-gray-80 block">
                    {t(ISSUE_MESSAGES[mic.result.issue!].fix)}
                  </Text>
                )}
              </div>
            )}

            <div className="flex flex-col gap-2 rounded-xl border border-solid border-gray-100 p-3">
              <Text type="body3-regular" className="text-gray-90 block tracking-wider uppercase">
                <Trans>Your recording · {mic.elapsedSec.toFixed(1)}s</Trans>
              </Text>
              {mic.recordedUrl && !playbackFailed && (
                <NectarAudioPlayer
                  audioUrl={mic.recordedUrl}
                  onPlay={() => setHasPlayed(true)}
                  onProgress={setPlayProgress}
                  onFinish={() => setPlayProgress(1)}
                  onError={() => setPlaybackFailed(true)}
                />
              )}
              {mic.recordedUrl && playbackFailed && (
                <audio
                  src={mic.recordedUrl}
                  controls
                  className="w-full"
                  onPlay={() => setHasPlayed(true)}
                  onTimeUpdate={(e) => {
                    const el = e.currentTarget
                    if (el.duration > 0) setPlayProgress(el.currentTime / el.duration)
                  }}
                  onEnded={() => setPlayProgress(1)}
                />
              )}
            </div>

            {gradeQuality && showDetails && (
              <div>
                <button
                  type="button"
                  onClick={() => setDetailsOpen((v) => !v)}
                  className="text-gray-80 hover:text-gray-70 flex items-center gap-1.5 transition-colors"
                >
                  <Text type="body3-regular" className="tracking-wider uppercase">
                    <Trans>Technical details</Trans>
                  </Text>
                  <ChevronDown className={clsx('size-3 transition-transform', detailsOpen && 'rotate-180')} />
                </button>
                {detailsOpen && (
                  <div className="mt-2 flex flex-col divide-y divide-solid divide-gray-100 border-t border-solid border-gray-100">
                    {visibleMetrics.map((m) => {
                      const { text, good } = metricValue(m, mic.result!)
                      return (
                        <div key={m} className="flex items-center justify-between py-2">
                          <Text type="body3-regular" className="text-gray-90 tracking-wider uppercase">
                            {t(METRIC_LABELS[m])}
                          </Text>
                          <Text type="body3-regular" className={clsx('font-medium', good ? 'text-green' : 'text-red')}>
                            {text}
                          </Text>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {showDevicePicker && mic.devices.length > 1 && (
          <div className="flex w-full flex-col gap-1.5">
            <FormSelect
              value={mic.selectedDeviceId ?? undefined}
              onChange={(id) => void mic.selectDevice(id)}
              label={t`Input`}
              placeholder={t`System default`}
              options={mic.devices.map((d) => ({
                value: d.deviceId,
                label: d.label || `Input · ${d.deviceId.slice(0, 8)}`
              }))}
            />
          </div>
        )}

        {mic.phase === 'result' && mic.result && (
          <div className="max-mobile:justify-stretch flex w-full flex-wrap justify-end gap-2">
            {!gradeQuality || mic.result.ok ? (
              <>
                <Button variant="bordered" onClick={mic.reset}>
                  <Trans>Record again</Trans>
                </Button>
                <Button variant="green" onClick={handlePass}>
                  <Trans>Start task</Trans>
                </Button>
              </>
            ) : (
              <>
                {!blockOnFail &&
                  (() => {
                    const unlocked = hasPlayed && playProgress >= 1
                    const hint = unlocked ? undefined : 'Listen to the full recording first'
                    return (
                      <Button
                        className="max-mobile:grow"
                        variant="bordered"
                        onClick={handleDismiss}
                        isDisabled={!unlocked}
                        title={hint}
                      >
                        <Trans>Sounds fine to me</Trans>
                      </Button>
                    )
                  })()}
                <Button className="max-mobile:grow" onClick={mic.reset}>
                  <Trans>Try again</Trans>
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
